import PropTypes from 'prop-types'
import React from 'react'
import {
  Button,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'

import moment from 'moment'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 2, 1),
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.fadedGreen.light,
    },
  },
  discardedText: {
    color: theme.palette.text.secondary,
  },
  name: {
    color: theme.palette.orange.main,
    fontSize: '1.25rem',
    fontFamily: 'Gotham',
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      lineHeight: 1.5,
    },
  },
  discardedRow: {
    backgroundColor: theme.palette.grey[200],
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  firstColumn: {
    width: '250px',
  },
  secondColumn: {
    width: '150px',
  },
  dateColumn: {
    textAlign: 'left',
  },
}))

const InventoryLocationStockCard = ({ location, items }) => {
  const classes = useStyles()

  const allDates = Array.from(
    new Set(
      [].concat(
        ...items.map(item =>
          [...item.stock.inStock, ...item.stock.discarded].map(
            entry => entry.date
          )
        )
      )
    )
  )
    .sort((a, b) => moment(a).diff(moment(b)))
    .slice(0, 5)
    .map(d => moment(d).format('Do MMM'))

  const getInStockForDate = (item, date) => {
    const entry = item.stock.inStock.find(
      i => moment(i.date).format('Do MMM') === date
    )
    return entry ? entry.amount : '-'
  }

  const getDiscardedForDate = (item, date) => {
    const entry = item.stock.discarded.find(
      i => moment(i.date).format('Do MMM') === date
    )
    return entry
      ? { amount: entry.amount, reason: entry.reasonForDiscarding }
      : { amount: '-', reason: '' }
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.firstColumn}>
              <Typography className={classes.name}>{location.name}</Typography>
            </TableCell>
            <TableCell className={classes.secondColumn}></TableCell>
            {allDates.map(date => (
              <TableCell key={date} className={classes.dateColumn}>
                {date}
              </TableCell>
            ))}
            <TableCell className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                href={`/inventory/${location.id}/edit`}
              >
                Update Inventory
              </Button>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <>
              <TableRow>
                <TableCell className={classes.firstColumn}>
                  <b>{item.name}</b>
                </TableCell>
                <TableCell className={classes.secondColumn}>
                  <b>{item.unit}</b>
                </TableCell>
                {allDates.map(date => (
                  <TableCell key={date} className={classes.dateColumn}>
                    {getInStockForDate(item, date)}
                  </TableCell>
                ))}
                <TableCell></TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.discardedText}>
                  Discarded
                </TableCell>
                <TableCell className={classes.secondColumn}></TableCell>
                {allDates.map(date => {
                  const discardedData = getDiscardedForDate(item, date)
                  return (
                    <TableCell key={date} className={classes.discardedText}>
                      <Tooltip
                        title={discardedData.reason || 'No reason provided'}
                      >
                        <span>
                          {discardedData.amount}
                          {discardedData.reason && (
                            <InfoIcon
                              fontSize="small"
                              style={{
                                marginLeft: '4px',
                                color: 'orange',
                              }}
                            />
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                  )
                })}
                <TableCell></TableCell>
              </TableRow>
            </>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

InventoryLocationStockCard.propTypes = {
  location: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      unit: PropTypes.string.isRequired,
      stock: PropTypes.shape({
        inStock: PropTypes.arrayOf(
          PropTypes.shape({
            date: PropTypes.string.isRequired,
            quantity: PropTypes.number.isRequired,
          })
        ).isRequired,
        discarded: PropTypes.arrayOf(
          PropTypes.shape({
            date: PropTypes.string.isRequired,
            quantity: PropTypes.number.isRequired,
          })
        ).isRequired,
      }).isRequired,
    })
  ).isRequired,
}

export default InventoryLocationStockCard
